/**
 * Shared helpers for the /codes/$id surface.
 *
 * Two responsibilities:
 *   - turn `QrCodeResponse.payload` (a flat `Record<string, unknown>`
 *     keyed by the type-specific fields from QrCodeDtos.kt) into the
 *     discriminated `CreateCodePayload` the api-client's editPayload /
 *     editRedirectTarget take; and
 *   - format dates the same way the legacy panel did so the visual
 *     contract carries over (date-only for expiry, datetime for created/
 *     locked).
 */
import type {
  CreateCodePayload,
  QrCodeResponse,
  WifiAuth,
} from "@qrody/api-client/codes";
import type { QrGeneratorSeed } from "@qrody/qr-generator";

export function payloadFromCode(code: QrCodeResponse): CreateCodePayload {
  const p = code.payload;
  const str = (k: string): string => {
    const v = p[k];
    return typeof v === "string" ? v : v == null ? "" : String(v);
  };
  const num = (k: string): number => {
    const v = p[k];
    if (typeof v === "number") return v;
    if (typeof v === "string" && v.length > 0) {
      const parsed = Number(v);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };
  const bool = (k: string): boolean => Boolean(p[k]);

  switch (code.type) {
    case "URL":
      return { kind: "url", url: str("url") };
    case "TEXT":
      return { kind: "text", text: str("text") };
    case "EMAIL":
      return {
        kind: "email",
        to: str("to"),
        subject: str("subject") || undefined,
        body: str("body") || undefined,
      };
    case "SMS":
      return {
        kind: "sms",
        to: str("to"),
        message: str("message") || undefined,
      };
    case "GEO":
      return {
        kind: "geo",
        latitude: num("latitude"),
        longitude: num("longitude"),
      };
    case "WIFI": {
      const raw = str("authentication");
      const auth: WifiAuth =
        raw === "WPA" || raw === "WEP" || raw === "NOPASS" ? raw : "WPA";
      return {
        kind: "wifi",
        ssid: str("ssid"),
        password: str("password") || undefined,
        authentication: auth,
        hidden: bool("hidden"),
      };
    }
    case "VCARD":
      return {
        kind: "vcard",
        fullName: str("fullName"),
        email: str("email") || undefined,
        phone: str("phone") || undefined,
        org: str("org") || undefined,
        title: str("title") || undefined,
        url: str("url") || undefined,
      };
    case "FILE":
      return { kind: "file", downloadUrl: str("downloadUrl") };
  }
}

/**
 * Tagesgenaue Anzeige für Ablauffristen — Backend speichert das Datum
 * als 00:00 UTC, hier wird nur das Kalenderdatum (UTC) formatiert,
 * damit ein Picker-Wert „2026-12-31" nicht durch Browser-Timezone-
 * Konversion z.B. als „30.12.2026, 23:00 Uhr" angezeigt wird.
 */
export function formatDateOnly(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Build the editor seed the QrGenerator takes for an existing code.
 * Mirrors web/.../code-detail-panel.tsx#seedFromCode so the visual
 * contract carries over verbatim.
 */
export function seedFromCode(code: QrCodeResponse): QrGeneratorSeed {
  const p = code.payload;
  const str = (k: string): string => {
    const v = p[k];
    return typeof v === "string" ? v : v == null ? "" : String(v);
  };
  const num = (k: string): string => {
    const v = p[k];
    return typeof v === "number" ? String(v) : v == null ? "" : String(v);
  };
  const bool = (k: string): boolean => Boolean(p[k]);

  switch (code.type) {
    case "URL":
      return { type: "url", form: { url: str("url") }, ...designSeed(code) };
    case "TEXT":
      return { type: "text", form: { text: str("text") }, ...designSeed(code) };
    case "EMAIL":
      return {
        type: "email",
        form: {
          emailTo: str("to"),
          emailSubject: str("subject"),
          emailBody: str("body"),
        },
        ...designSeed(code),
      };
    case "SMS":
      return {
        type: "sms",
        form: { smsTo: str("to"), smsMessage: str("message") },
        ...designSeed(code),
      };
    case "GEO":
      return {
        type: "geo",
        form: { geoLat: num("latitude"), geoLon: num("longitude") },
        ...designSeed(code),
      };
    case "WIFI": {
      const auth = str("authentication") as "WPA" | "WEP" | "NOPASS";
      return {
        type: "wifi",
        form: {
          wifiSsid: str("ssid"),
          wifiPassword: str("password"),
          wifiAuth: auth || "WPA",
          wifiHidden: bool("hidden"),
        },
        ...designSeed(code),
      };
    }
    case "VCARD":
      return {
        type: "vcard",
        form: {
          vcardFullName: str("fullName"),
          vcardEmail: str("email"),
          vcardPhone: str("phone"),
          vcardOrg: str("org"),
          vcardTitle: str("title"),
          vcardUrl: str("url"),
        },
        ...designSeed(code),
      };
    case "FILE":
      return {
        type: "file",
        form: { fileUrl: str("downloadUrl") },
        ...designSeed(code),
      };
  }
}

function designSeed(code: QrCodeResponse): {
  foreground: string;
  background: string;
  frame: "NONE" | "SIMPLE" | "ROUNDED" | "SHIELD" | "CALL_TO_ACTION";
  ecc: "LOW" | "MEDIUM" | "QUARTILE" | "HIGH";
  minVersion: number | null;
  format?: "png" | "svg" | "pdf";
  size?: number;
} {
  // Backend QrFrame and AnonymousFrame are structurally identical
  // today (NONE/SIMPLE/ROUNDED/SHIELD/CALL_TO_ACTION). Keep an explicit
  // table so a future divergence stays a one-line fix here.
  const backendToAnonymous: Record<
    string,
    "NONE" | "SIMPLE" | "ROUNDED" | "SHIELD" | "CALL_TO_ACTION"
  > = {
    NONE: "NONE",
    SIMPLE: "SIMPLE",
    ROUNDED: "ROUNDED",
    SHIELD: "SHIELD",
    CALL_TO_ACTION: "CALL_TO_ACTION",
  };
  // Backend `QrFormat` is upper-case, the AnonymousFormat the generator
  // edits in is lower-case. Drop unknown variants silently — the seed
  // optional means the editor falls back to its own default.
  const backendFormat = code.design.format;
  const formatSeed: "png" | "svg" | "pdf" | undefined =
    backendFormat === "PNG"
      ? "png"
      : backendFormat === "SVG"
        ? "svg"
        : backendFormat === "PDF"
          ? "pdf"
          : undefined;
  return {
    foreground: code.design.foreground,
    background: code.design.background,
    frame: backendToAnonymous[code.design.frame] ?? "NONE",
    ecc:
      (code.design.ecc as "LOW" | "MEDIUM" | "QUARTILE" | "HIGH" | undefined) ??
      "MEDIUM",
    minVersion: code.design.minVersion ?? null,
    ...(formatSeed ? { format: formatSeed } : {}),
    ...(code.design.size != null ? { size: code.design.size } : {}),
  };
}

/**
 * Build the absolute scan URL for a slug — `{public-site}/r/{slug}`.
 *
 * Default origin is `https://qrody.com`; the Vite dev-server proxies
 * `/r/*` through to the backend on the same origin, so for local
 * smoke-tests setting `VITE_PUBLIC_SITE_URL=http://localhost:5174` in
 * `.env.local` keeps the link clickable against the dev BFF.
 */
export function scanUrlForSlug(slug: string): string {
  const env = (import.meta as { env?: Record<string, string> }).env;
  const origin = (env?.VITE_PUBLIC_SITE_URL ?? "https://qrody.com").replace(
    /\/$/,
    "",
  );
  return `${origin}/r/${slug}`;
}

/**
 * Build the URL payload override the legacy panel passes to QrGenerator
 * for DYNAMIC codes. The live preview must encode the slug-redirect
 * (the matrix that ends up in print), not the edit-time target URL.
 */
export function previewPayloadOverrideForCode(
  code: QrCodeResponse,
): { type: "url"; url: string } | undefined {
  if (code.mode !== "DYNAMIC" || !code.shortSlug) return undefined;
  return { type: "url", url: scanUrlForSlug(code.shortSlug) };
}
